import { useState, ReactElement } from 'react'
import { Menu, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/theme-context'
import { MainNav, type NavigationItem } from './MainNav'
import { UserMenu, type UserMenuProps } from './UserMenu'

export interface AppShellProps {
  children: React.ReactNode
  navigationItems: NavigationItem[]
  user?: UserMenuProps['user']
  workspace?: { name: string }
  isAuthenticated?: boolean
  onNavigate?: (href: string) => void
  onLogout?: () => void
}

function MobileOverlay({ onClose }: { onClose: () => void }): ReactElement {
  return (
    <div
      className="fixed inset-0 z-40 md:hidden"
      style={{ background: 'rgba(28, 31, 46, 0.5)' }}
      onClick={onClose}
    />
  )
}

function SidebarLogo({
  brandName,
  onCloseSidebar,
}: {
  brandName: string
  onCloseSidebar: () => void
}): ReactElement {
  return (
    <div
      className="flex h-16 items-center justify-between border-b px-4"
      style={{ borderColor: 'var(--border)' }}
    >
      <span
        className="text-xl font-bold tracking-tight"
        style={{ fontFamily: 'var(--font-display, Syne, sans-serif)', color: 'var(--accent)' }}
      >
        {brandName}
      </span>
      <button
        onClick={onCloseSidebar}
        className="rounded-md p-1 md:hidden"
        style={{ color: 'var(--text-muted)' }}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

function MobileHeader({
  brandName,
  onOpenSidebar,
}: {
  brandName: string
  onOpenSidebar: () => void
}): ReactElement {
  return (
    <header
      className="sticky top-0 z-30 flex h-16 items-center border-b px-4 backdrop-blur md:hidden"
      style={{ background: 'var(--bg-sidebar)', borderColor: 'var(--border)' }}
    >
      <button
        onClick={onOpenSidebar}
        className="-ml-2 rounded-md p-2"
        style={{ color: 'var(--text-muted)' }}
      >
        <Menu className="h-5 w-5" />
      </button>
      <span
        className="ml-3 text-lg font-bold"
        style={{ fontFamily: 'var(--font-display, Syne, sans-serif)', color: 'var(--accent)' }}
      >
        {brandName}
      </span>
    </header>
  )
}

export function AppShell({
  children,
  navigationItems,
  user,
  workspace,
  isAuthenticated,
  onNavigate,
  onLogout,
}: AppShellProps): ReactElement {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { t } = useTranslation('common')
  const { theme } = useTheme()
  const brandName = t('brand.name')
  const closeSidebar = (): void => {
    setSidebarOpen(false)
  }
  const openSidebar = (): void => {
    setSidebarOpen(true)
  }

  return (
    <div
      className="dashboard min-h-screen font-sans"
      data-theme={theme}
      style={{ background: 'var(--bg)' }}
    >
      {sidebarOpen && <MobileOverlay onClose={closeSidebar} />}
      <aside
        className={`db-sidebar fixed inset-y-0 left-0 z-50 flex w-60 transform flex-col border-r transition-transform duration-200 ease-in-out md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} `}
        style={{ background: 'var(--bg-sidebar)', borderColor: 'var(--border)' }}
      >
        <SidebarLogo brandName={brandName} onCloseSidebar={closeSidebar} />
        <div className="flex-1 py-4">
          <MainNav items={navigationItems} onNavigate={onNavigate} />
        </div>
        <div className="border-t" style={{ borderColor: 'var(--border)' }}>
          <UserMenu
            user={user}
            workspace={workspace}
            isAuthenticated={isAuthenticated}
            onLogout={onLogout}
          />
        </div>
      </aside>
      <div className="md:pl-60">
        <MobileHeader brandName={brandName} onOpenSidebar={openSidebar} />
        <main className="min-h-screen">{children}</main>
      </div>
    </div>
  )
}
